/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

import {
	REGEX_HTML,
	REGEX_NUMERIC_FORMAT,
} from '@config/constants/regex';
import { isNumber } from './functions';
import Decimal from 'decimal.js';
import isNil from 'lodash/isNil';

export const formatCurrency = (
	amount: number,
	currencySymbol: string,
	decimalSeparator = '.',
	thousandsSeparator = ' '
) => {
	if (!isNumber(amount)) {
		return '0' + currencySymbol;
	}

	const parts = amount.toFixed(2).toString().split('.');
	const integerPart = parts[0].replace(
		/\B(?=(\d{3})+(?!\d))/g,
		thousandsSeparator
	);
	const decimalPart = parts[1];

	if (decimalPart === '00') {
		return integerPart + currencySymbol;
	} else {
		return (
			integerPart +
			decimalSeparator +
			decimalPart +
			currencySymbol
		);
	}
};

export const formatTokenAvailable = (
	tokenAvailable: number
) => {
	if (tokenAvailable === 0)
		return tokenAvailable.toString();

	const formattedToken = tokenAvailable.toFixed(0);
	const decimalPart = formattedToken.split('.')[1];

	if (decimalPart === '000') {
		return formattedToken.split('.')[0];
	} else {
		return formattedToken;
	}
};

export const formatTwoDecimals = (
	tokenAvailable: number
) => {
	if (tokenAvailable === 0)
		return tokenAvailable.toString();

	const formattedToken = tokenAvailable.toFixed(2);
	const decimalPart = formattedToken.split('.')[1];

	if (decimalPart === '00') {
		return formatNumberWithSpaces(
			Number(formattedToken.split('.')[0])
		);
	} else {
		return formatNumberWithSpaces(Number(formattedToken));
	}
};

export const formatNumberWithSpaces = (
	number: number
): string => {
	if (isNumber(number)) {
		const [integerPart, decimalPart] = number
			.toString()
			.split('.');
		const formattedInteger = integerPart.replace(
			/\B(?=(\d{3})+(?!\d))/g,
			' '
		);
		return decimalPart
			? `${formattedInteger}.${decimalPart}`
			: formattedInteger;
	} else {
		return '0';
	}
};

export const formatTextWithHTMLParagraph = (
	text: string
) => {
	return REGEX_HTML.test(text) ? text : `<p>${text}</p>`;
};

/* -------------------------------------------------------------------------- */
/*                                Productions                                 */
/* -------------------------------------------------------------------------- */
export const numericTokenEquivalent = (
	convertionFactor: number,
	quantity: number
) => {
	const _convertionFactor = new Decimal(convertionFactor);
	const _quantity = new Decimal(quantity);

	if (isNil(convertionFactor)) {
		return 0;
	}

	const mulConvertionFactorAndQuantity = Decimal.mul(
		_convertionFactor,
		_quantity
	);

	const finalResult = Decimal.div(
		mulConvertionFactorAndQuantity,
		1000000
	);

	return finalResult.toNumber();
};

export const numericConversionFactor = (
	_conversionFactorOverride: number,
	_conversionFactor: { ratio: number }
) => {
	if (!_conversionFactorOverride) {
		return _conversionFactor
			? _conversionFactor.ratio
			: 0;
	} else {
		if (!!_conversionFactorOverride)
			return _conversionFactorOverride;
	}
	return 0;
};

export const formatNumeric = (value: number) => {
	let formattedNumber = value.toFixed(3);

	if (
		/(\.\d{3})$/.test(formattedNumber) &&
		formattedNumber.slice(-3) === '000'
	) {
		formattedNumber = Math.floor(value).toString();
	}

	return formattedNumber.replace(
		REGEX_NUMERIC_FORMAT,
		' '
	);
};
